import React from "react"
import { useState, useEffect, useCallback } from "react"
import { Box, Flex, Card, Grid, Heading, Text, Badge, Button, IconButton, Dialog, TextField, TextArea, Select, ScrollArea, Separator, Switch } from "@radix-ui/themes"
import { 
  CalendarIcon, 
  PlusIcon, 
  PercentIcon, 
  DollarSignIcon,
  Search,
  Edit,
  Trash2,
  Users,
  Target
} from "lucide-react"
import { toast } from "react-hot-toast"
import { promotionsAPI } from "../services/api"
import PageHeader from "../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../components/ui/dashboard/LoadingSpinner"

interface Promo {
  _id: string
  code: string
  name: string
  description: string
  type: "percentage" | "fixed" | "free_delivery"
  value: number
  minOrderAmount: number
  maxDiscountAmount?: number
  usageLimit?: number
  usageCount: number
  userUsageLimit: number
  startDate: string
  endDate: string
  isActive: boolean
  appliedRestaurants: string[]
  userType: "all" | "new" | "returning"
  createdBy: {
    _id: string
    username: string
  }
  createdAt: string
  updatedAt: string
}

export default function PromotionsPage() {
  const [promos, setPromos] = useState<Promo[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingPromo, setEditingPromo] = useState<Promo | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [formData, setFormData] = useState<{
    code: string
    name: string
    description: string
    type: "percentage" | "fixed" | "free_delivery"
    value: number
    minOrderAmount: number
    maxDiscountAmount: number
    usageLimit: number
    userUsageLimit: number
    startDate: string
    endDate: string
    isActive: boolean
    userType: "all" | "new" | "returning"
  }>({
    code: "",
    name: "",
    description: "",
    type: "percentage",
    value: 0,
    minOrderAmount: 0,
    maxDiscountAmount: 0,
    usageLimit: 0,
    userUsageLimit: 1,
    startDate: "",
    endDate: "",
    isActive: true,
    userType: "all",
  })

  const fetchPromos = useCallback(async () => {
    try {
      setLoading(true)
      const params = {
        page: currentPage,
        limit: 10,
        status: filterStatus,
      }
      console.log("🔍 Fetching promotions with params:", params)
      const response = await promotionsAPI.getPromotions(params)
      console.log("📦 API Response:", response)
      
      // Handle the correct response structure
      if (response.data && response.data.data) {
        console.log("✅ Using nested data structure")
        setPromos(response.data.data.promos || [])
        setTotalPages(response.data.data.pagination?.pages || 1)
      } else {
        console.log("✅ Using direct data structure")
        setPromos(response.data?.promos || [])
        setTotalPages(1)
      }
      console.log("📊 Set promos:", response.data?.data?.promos || response.data?.promos || [])
    } catch (error) {
      console.error("❌ Error fetching promos:", error)
      toast.error("Failed to load promotions")
    } finally {
      setLoading(false)
    }
  }, [currentPage, filterStatus])

  useEffect(() => {
    fetchPromos()
  }, [fetchPromos])

  const handleSavePromo = async () => {
    try {
      // Validate required fields
      if (!formData.code.trim()) {
        toast.error("Promo code is required")
        return
      }
      
      if (!formData.name.trim()) {
        toast.error("Promo name is required")
        return
      }
      
      if (!formData.description.trim()) {
        toast.error("Description is required")
        return
      }
      
      if (!formData.startDate) {
        toast.error("Start date is required")
        return
      }
      
      if (!formData.endDate) {
        toast.error("End date is required")
        return
      }
      
      if (formData.value <= 0) {
        toast.error("Value must be greater than 0")
        return
      }

      if (editingPromo) {
        await promotionsAPI.updatePromotion(editingPromo._id, formData)
        toast.success("Promotion updated!")
      } else {
        await promotionsAPI.createPromotion(formData)
        toast.success("Promotion created!")
      }
      
      setIsModalOpen(false)
      setEditingPromo(null)
      resetForm()
      fetchPromos()
    } catch (error) {
      console.error("Error saving promo:", error)
      toast.error("Failed to save promotion")
    }
  }

  const handleDeletePromo = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this promotion?")) return

    try {
      await promotionsAPI.deletePromotion(id)
      toast.success("Promotion deleted!")
      fetchPromos()
    } catch (error) {
      console.error("Error deleting promo:", error)
      toast.error("Failed to delete promotion")
    }
  }

  const handleEditPromo = (promo: Promo) => {
    setEditingPromo(promo)
    
    // Handle date formatting more safely
    const formatDateForInput = (dateString: string) => {
      try {
        const date = new Date(dateString)
        return date.toISOString().split('T')[0]
      } catch (error) {
        console.error('Error formatting date:', error)
        return ''
      }
    }
    
    setFormData({
      code: promo.code,
      name: promo.name,
      description: promo.description,
      type: promo.type,
      value: promo.value,
      minOrderAmount: promo.minOrderAmount,
      maxDiscountAmount: promo.maxDiscountAmount || 0,
      usageLimit: promo.usageLimit || 0,
      userUsageLimit: promo.userUsageLimit,
      startDate: formatDateForInput(promo.startDate),
      endDate: formatDateForInput(promo.endDate),
      isActive: promo.isActive,
      userType: promo.userType,
    })
    setIsModalOpen(true)
  }

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      description: "",
      type: "percentage",
      value: 0,
      minOrderAmount: 0,
      maxDiscountAmount: 0,
      usageLimit: 0,
      userUsageLimit: 1,
      startDate: "",
      endDate: "",
      isActive: true,
      userType: "all",
    })
  }

  const getPromoTypeIcon = (type: string) => {
    switch (type) {
      case "percentage":
        return <PercentIcon className="w-12 h-12 text-gray-400" />
      case "fixed":
        return <DollarSignIcon className="w-12 h-12 text-gray-400" />
      case "free_delivery":
          return <CalendarIcon className="w-12 h-12 text-gray-400" />
      default:
        return <PercentIcon className="w-12 h-12 text-gray-400" />
    }
  }

  const getPromoStatusBadge = (promo: Promo) => {
    try {
      const now = new Date()
      const startDate = new Date(promo.startDate)
      const endDate = new Date(promo.endDate)

      if (!promo.isActive) {
        return <Badge color="red">Inactive</Badge>
      }

      if (now < startDate) {
        return <Badge color="yellow">Scheduled</Badge> 
      }

      if (now > endDate) {
        return <Badge color="red">Expired</Badge>
      }

      if (promo.usageLimit && promo.usageCount >= promo.usageLimit) {
        return <Badge color="red">Used Up</Badge>
      }

      return <Badge color="green">Active</Badge>
    } catch (error) {
      console.error('Error calculating promo status:', error)
      return <Badge color="yellow">Unknown</Badge>
    }
  }

  const filteredPromos = promos.filter((promo) =>
    promo.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    promo.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <Flex direction="column" gap="6">
      {/* Header */}
      <PageHeader
        title="Promotions & Discounts"
        description="Manage promo codes and discount campaigns"
        action={
          <Button onClick={() => setIsModalOpen(true)} size="3">
            <PlusIcon size={16} />
            Create Promotion
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <Grid columns={{ initial: "1", md: "3" }} gap="4" align="center">
          <TextField.Root
            placeholder="Search promotions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          >
            <TextField.Slot>
              <Search size={16} />
            </TextField.Slot>
          </TextField.Root>

          <Select.Root value={filterStatus} onValueChange={setFilterStatus}>
            <Select.Trigger style={{ width: "100%" }} placeholder="All Status" />
            <Select.Content>
              <Select.Item value="all">All Status</Select.Item>
              <Select.Item value="active">Active</Select.Item>
              <Select.Item value="inactive">Inactive</Select.Item>
            </Select.Content>
          </Select.Root>

          <Flex align="center" gap="2">
            <Target size={16} style={{ color: "var(--gray-9)" }} />
            <Text size="2" color="gray">{filteredPromos.length} promotions found</Text>
          </Flex>
        </Grid>
      </Card>

      {/* Promotions Grid */}
      {loading ? (
        <LoadingSpinner size="3" text="Loading promotions..." />
      ) : (
        <Grid columns={{ initial: "1", md: "2", lg: "3" }} gap="4">
          {filteredPromos.map((promo) => (
            <Card key={promo._id}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    {getPromoTypeIcon(promo.type)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{promo.code}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{promo.name}</p>
                    </div>
                  </div>
                  {getPromoStatusBadge(promo)}
                </div>

                <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{promo.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Discount:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {promo.type === "percentage" 
                        ? `${promo.value}%` 
                        : promo.type === "fixed" 
                        ? `${promo.value} SDG` 
                        : "Free Delivery"
                      }
                    </span>
                  </div>

                  {promo.minOrderAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Min Order:</span>
                      <span className="text-gray-900 dark:text-white">{promo.minOrderAmount} SDG</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Usage:</span>
                    <span className="text-gray-900 dark:text-white">
                      {promo.usageCount}
                      {promo.usageLimit ? ` / ${promo.usageLimit}` : " / ∞"}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Valid:</span>
                    <span className="text-gray-900 dark:text-white">
                      {(() => {
                        try {
                          const startDate = new Date(promo.startDate)
                          const endDate = new Date(promo.endDate)
                          return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                        } catch (error) {
                          return 'Invalid date'
                        }
                      })()}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">User Type:</span>
                    <span className="text-gray-900 dark:text-white capitalize">{promo.userType}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditPromo(promo)}
                      className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900 rounded"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePromo(promo._id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Users className="w-3 h-3" />
                    <span>{promo.userUsageLimit} per user</span>
                  </div>
                </div>
            </Card>
          ))}
        </Grid>
      )}

      {!loading && filteredPromos.length === 0 && (
        <Card>
          <Flex direction="column" align="center" justify="center" p="9" gap="3">
            <Box style={{
              width: 64,
              height: 64,
              borderRadius: "var(--radius-full)",
              backgroundColor: "var(--gray-4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Target size={32} style={{ color: "var(--gray-9)" }} />
            </Box>
            <Heading size="4">No promotions found</Heading>
            <Text size="2" color="gray">
              {searchQuery ? "Try adjusting your search or filters" : "Create your first promotion to get started"}
            </Text>
            {!searchQuery && (
              <Button onClick={() => setIsModalOpen(true)} size="3" mt="2">
                <PlusIcon size={16} />
                Create Your First Promotion
              </Button>
            )}
          </Flex>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="center" gap="2">
          <Button
            variant="soft"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              variant={currentPage === page ? "solid" : "soft"}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </Button>
          ))}

          <Button
            variant="soft"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </Flex>
      )}

      {/* Modal for Create/Edit Promotion */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              {editingPromo ? "Edit Promotion" : "Create New Promotion"}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Promo Code *</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER20"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Name *</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Summer Discount"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Get 20% off on your summer orders"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md resize-none dark:bg-gray-700 dark:text-white"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="percentage">Percentage Discount</option>
                  <option value="fixed">Fixed Amount</option>
                  <option value="free_delivery">Free Delivery</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Value</label>
                <input
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                  placeholder={formData.type === "percentage" ? "20" : "10"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Minimum Order Amount</label>
                <input
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  type="number"
                  value={formData.minOrderAmount}
                  onChange={(e) => setFormData({ ...formData, minOrderAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="50"
                />
              </div>

              {formData.type === "percentage" && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Maximum Discount Amount</label>
                  <input
                    className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    type="number"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => setFormData({ ...formData, maxDiscountAmount: parseFloat(e.target.value) || 0 })}
                    placeholder="100"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Usage Limit</label>
                <input
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  type="number"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({ ...formData, usageLimit: parseInt(e.target.value) || 0 })}
                  placeholder="1000 (0 for unlimited)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">User Usage Limit</label>
                <input
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  type="number"
                  value={formData.userUsageLimit}
                  onChange={(e) => setFormData({ ...formData, userUsageLimit: parseInt(e.target.value) || 1 })}
                  placeholder="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">User Type</label>
                <select
                  value={formData.userType}
                  onChange={(e) => setFormData({ ...formData, userType: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Users</option>
                  <option value="new">New Users Only</option>
                  <option value="returning">Returning Users Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Start Date</label>
                <input
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">End Date</label>
                <input
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleSavePromo}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {editingPromo ? "Update" : "Create"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false)
                    setEditingPromo(null)
                    resetForm()
                  }}
                  className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-300"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Flex>
  )
}